<script setup lang="ts">
/*
  An alias: what the Operator's URLs name a dataspace, or a process, by instead of its id.

  Shared by the two dialogs that make one (a dataspace, a process) and the two places that change one
  (the dataspace's settings, `EditProcessDialog`), so all four ask the same way and say the same
  things under the field: while it is blank, what an alias is for; once something is typed, the URL it
  will make — normalized as the server stores it, so `Night Shift` already says `night-shift` — or why
  the server would refuse it (`shared/utils/alias.ts`, the server's rule mirrored for this alone).

  `taken` is the alias the server last answered `alias_taken` for, as it normalizes. The field says
  so for as long as it still holds that alias, and no longer once it is changed: whether another
  one is free is only known by sending it.
*/
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { HabInput } from "@livereader/habitus-vue";
import { ALIAS_MAX_LENGTH, aliasProblem, normalizeAlias } from "@/shared/utils/alias";

const props = defineProps<{
	modelValue: string;
	/** The URL up to where the alias goes: `/operator/`, or a dataspace's `…/processes/`. */
	prefix: string;
	/** The alias the server said is taken, normalized; `null` for none. */
	taken?: string | null;
}>();

const emit = defineEmits<{
	"update:modelValue": [value: string];
}>();

const { t } = useI18n();

const normalized = computed(() => normalizeAlias(props.modelValue));
const problem = computed(() => aliasProblem(normalized.value));
const isTaken = computed(() => !!normalized.value && normalized.value === props.taken);

const state = computed(() => (problem.value || isTaken.value ? "error" : "default"));
const help = computed(() => {
	if (isTaken.value) return t("operator.alias.taken");
	switch (problem.value) {
		case "form":
			return t("operator.alias.problem.form");
		case "length":
			return t("operator.alias.problem.length", { max: ALIAS_MAX_LENGTH });
		case "id":
			return t("operator.alias.problem.id");
		case "reserved":
			return t("operator.alias.problem.reserved", { alias: normalized.value });
	}
	return normalized.value
		? t("operator.alias.opens", { path: `${props.prefix}${normalized.value}` })
		: t("operator.alias.hint");
});
</script>

<template>
	<HabInput
		:model-value="modelValue"
		:label="$t('operator.alias.label')"
		:help="help"
		:state="state"
		mono
		autocomplete="off"
		spellcheck="false"
		@update:model-value="(value: string) => emit('update:modelValue', value)"
	/>
</template>
